import React, { useState } from 'react';
import { LightbulbIcon, ChevronDownIcon, ChevronRightIcon } from '@bizfirst/common-react';
import {
  ExecutionObserverConfig,
  EntityTypeFilter,
  DEFAULT_ENTITY_TYPE_FILTER,
} from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/EntityFiltersTab.css';

interface EntityFiltersTabProps {
  config: ExecutionObserverConfig;
  onChange: (config: ExecutionObserverConfig) => void;
  readonly?: boolean;
}

export const EntityFiltersTab: React.FC<EntityFiltersTabProps> = ({
  config,
  onChange,
  readonly = false
}) => {
  const [showEntityTypeFilters, setShowEntityTypeFilters] = useState(true);
  const [showAdvancedIds, setShowAdvancedIds] = useState(false);

  const filter = config.filter || {};
  const entityTypes = filter.entityTypes || DEFAULT_ENTITY_TYPE_FILTER;
  const entityIds = filter.entityIds || {};

  const updateEntityTypes = (updates: Partial<EntityTypeFilter>) => {
    onChange({
      ...config,
      filter: {
        ...filter,
        entityTypes: { ...entityTypes, ...updates }
      }
    });
  };

  const updateEntityIds = (updates: any) => {
    onChange({
      ...config,
      filter: {
        ...filter,
        entityIds: { ...entityIds, ...updates }
      }
    });
  };

  return (
    <fieldset className={`entity-filters-tab${!showEntityTypeFilters ? ' entity-filters-tab--collapsed' : ''}`} disabled={readonly}>
      <legend>
        <button
          type="button"
          className="toggle-advanced"
          onClick={() => setShowEntityTypeFilters(!showEntityTypeFilters)}
          disabled={readonly}
        >
          {showEntityTypeFilters ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
          {' '}Entity Type Filters
        </button>
      </legend>

      {showEntityTypeFilters && (
        <>
          {/* Main Entity Types */}
          <div className="entity-section">
            <span className="entity-section__label">Entity Types</span>
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeProcess ?? true}
                  onChange={(e) => updateEntityTypes({ includeProcess: e.target.checked })}
                  disabled={readonly}
                />
                <span>Process</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeThread ?? true}
                  onChange={(e) => updateEntityTypes({ includeThread: e.target.checked })}
                  disabled={readonly}
                />
                <span>Thread</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeNode ?? true}
                  onChange={(e) => updateEntityTypes({ includeNode: e.target.checked })}
                  disabled={readonly}
                />
                <span>Node</span>
              </label>
            </div>
          </div>

          {/* Node Sub-types */}
          <div className="entity-section">
            <span className="entity-section__label entity-section__label--sub">Node Sub-types <small style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, opacity: 0.7 }}>— requires Node enabled</small></span>
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeConfigResolution ?? false}
                  onChange={(e) => updateEntityTypes({ includeConfigResolution: e.target.checked })}
                  disabled={readonly || !entityTypes.includeNode}
                />
                <span>Config Resolution</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeExpressions ?? false}
                  onChange={(e) => updateEntityTypes({ includeExpressions: e.target.checked })}
                  disabled={readonly || !entityTypes.includeNode}
                />
                <span>Expression Evaluation</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includePolicies ?? false}
                  onChange={(e) => updateEntityTypes({ includePolicies: e.target.checked })}
                  disabled={readonly || !entityTypes.includeNode}
                />
                <span>Policy Evaluation</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeGuardRails ?? false}
                  onChange={(e) => updateEntityTypes({ includeGuardRails: e.target.checked })}
                  disabled={readonly || !entityTypes.includeNode}
                />
                <span>Guard Rails</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={entityTypes.includeRouting ?? false}
                  onChange={(e) => updateEntityTypes({ includeRouting: e.target.checked })}
                  disabled={readonly || !entityTypes.includeNode}
                />
                <span>Routing Decision</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Entity ID Filters — full width, collapsible */}
      <div className="form-group entity-id-section">
        <button
          type="button"
          className="toggle-advanced"
          onClick={() => setShowAdvancedIds(!showAdvancedIds)}
          disabled={readonly}
        >
          {showAdvancedIds ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
          {' '}Entity ID Filters (optional)
        </button>

        {showAdvancedIds && (
          <div className="advanced-ids">
            <div className="input-group">
              <label htmlFor="process-ids">Process IDs (comma-separated):</label>
              <input
                id="process-ids"
                type="text"
                placeholder="e.g., 1,2,3"
                value={entityIds.processIds?.join(',') || ''}
                onChange={(e) => {
                  const ids = e.target.value
                    .split(',')
                    .map((x) => parseInt(x.trim()))
                    .filter((x) => !isNaN(x));
                  updateEntityIds({ processIds: ids.length > 0 ? ids : undefined });
                }}
                disabled={readonly}
              />
              <small>Leave blank to watch all processes</small>
            </div>

            <div className="input-group">
              <label htmlFor="execution-id">Execution ID:</label>
              <input
                id="execution-id"
                type="text"
                placeholder="e.g., exec-12345"
                value={entityIds.executionId || ''}
                onChange={(e) =>
                  updateEntityIds({ executionId: e.target.value || undefined })
                }
                disabled={readonly}
              />
            </div>

            <div className="input-group">
              <label htmlFor="node-keys">Node Keys (comma-separated):</label>
              <input
                id="node-keys"
                type="text"
                placeholder="e.g., node-1,node-2"
                value={entityIds.nodeKeys?.join(',') || ''}
                onChange={(e) => {
                  const keys = e.target.value
                    .split(',')
                    .map((x) => x.trim())
                    .filter(Boolean);
                  updateEntityIds({ nodeKeys: keys.length > 0 ? keys : undefined });
                }}
                disabled={readonly}
              />
            </div>
          </div>
        )}
      </div>

      <div className="info-box">
        <p>
          <LightbulbIcon size={14} /> <strong>Tip:</strong> Use Entity ID filters to focus on specific processes or nodes.
        </p>
      </div>
    </fieldset>
  );
};

export default EntityFiltersTab;
